import React, {useEffect, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {Autocomplete, createFilterOptions} from "@mui/material";
import {
  Box,
  Checkbox,
  Chip,
  Colors,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Text,
  Tooltip,
} from "@bounce/shared/ui";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import {TextField} from "../../ui/components";
import {listCardSx} from "../../ui/components/cardStyles";
import {openInNewTab} from "../../components/ResourcePanel";
import {autocompleteChevronSx} from "../../ui/components/inputStyles";
import {ReachTemplateContentChannel, ReachTemplateOption} from "../../apis/requests/templates/types";
import {
  TriggerCondition,
  TriggerInteractionType,
  TriggerOutcome,
  TriggerTiming,
} from "../../apis/requests/triggers/types";
import {CHANNEL_DISPLAY} from "../templates/templateDisplay";
import {MMS_CHANNELS} from "../templates/templateFormSchema";
import {templateUrl} from "./helpers";
import SegmentedToggle from "./SegmentedToggle";
import {
  OWN_PAYMENT_INTERACTION_TYPES,
  TEMPLATE_SLOT_CHANNELS,
  TEXT_KIND_CHANNELS,
  TIMING_DISPLAY,
  TextKind,
} from "./triggerDisplay";
import {PreviewTab} from "./helpers";
import TriggerPreviewDialog from "./TriggerPreviewDialog";

const OUTCOME_OPTIONS: {value: TriggerOutcome; label: string}[] = [
  {value: "send", label: "Send messages"},
  {value: "do_not_send", label: "Don't send"},
];

const TEXT_KIND_OPTIONS: {value: TextKind; label: string}[] = [
  {value: "sms", label: "SMS"},
  {value: "mms", label: "MMS"},
];

const textKindOf = (channel: ReachTemplateContentChannel): TextKind => (MMS_CHANNELS.has(channel) ? "mms" : "sms");

const rowSx = {display: "flex", alignItems: "center", gap: 1.5, p: "12px 14px"} as const;

const iconButtonSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  border: `1px solid ${Colors.tertiary._300}`,
  borderRadius: "8px",
  backgroundColor: Colors.bw.white,
  color: Colors.blue._800,
  "&:hover": {backgroundColor: Colors.blue._100},
};

export const PreviewButton = ({disabled, onClick}: {disabled: boolean; onClick: () => void}) => (
  <Tooltip title={disabled ? "Pick a template first" : "Preview"}>
    <span>
      <IconButton aria-label="Preview template" disabled={disabled} onClick={onClick} sx={iconButtonSx}>
        <VisibilityOutlinedIcon sx={{fontSize: 18}} />
      </IconButton>
    </span>
  </Tooltip>
);

const OpenTemplateButton = ({templateId}: {templateId?: number}) => (
  <Tooltip title={templateId ? "Open template in a new tab" : "Pick a template first"}>
    <span>
      <IconButton
        aria-label="Open template"
        disabled={!templateId}
        onClick={() => templateId && openInNewTab(templateUrl(templateId))}
        sx={iconButtonSx}
      >
        <OpenInNewIcon sx={{fontSize: 17}} />
      </IconButton>
    </span>
  </Tooltip>
);

export const SectionTitle = ({children}: {children: string}) => (
  <Text size={16} variant="inter600" sx={{color: Colors.grey._900}}>
    {children}
  </Text>
);

type TemplatePickerProps = {
  placeholder: string;
  templates: ReachTemplateOption[];
  channels: ReachTemplateContentChannel[];
  value: number | null;
  onChange: (id: number | null) => void;
  showChannel?: boolean;
  disabled?: boolean;
};

const filterTemplates = createFilterOptions<ReachTemplateOption>({
  stringify: (template) => `${template.template_key} ${template.name}`,
});

const TemplatePicker = ({
  placeholder,
  templates,
  channels,
  value,
  onChange,
  showChannel = false,
  disabled,
}: TemplatePickerProps) => {
  const options = templates.filter((t) => channels.includes(t.content_channel));
  return (
    <Autocomplete<ReachTemplateOption, false, false, false>
      size="small"
      fullWidth
      disabled={disabled}
      popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
      sx={autocompleteChevronSx}
      options={options}
      value={options.find((t) => t.id === value) ?? null}
      onChange={(_, template) => onChange(template?.id ?? null)}
      getOptionLabel={(template) => template.template_key}
      filterOptions={filterTemplates}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      noOptionsText="No transactional templates for this channel"
      renderOption={({key, ...props}, template) => (
        <Box component="li" key={key} {...props} sx={{display: "flex", gap: 1, alignItems: "center"}}>
          <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column"}}>
            <Box component="span" sx={{fontFamily: "monospace", fontSize: 13}}>
              {template.template_key}
            </Box>
            <Box component="span" sx={{fontSize: 12, color: Colors.grey._600}}>
              {template.name}
            </Box>
          </Box>
          {showChannel && (
            <Chip
              size_="small"
              variant_={CHANNEL_DISPLAY[template.content_channel].variant}
              label={CHANNEL_DISPLAY[template.content_channel].label}
            />
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} InputSx={{fontFamily: "monospace", fontSize: 13}} />
      )}
    />
  );
};

type TimingSectionProps = {
  timing: TriggerTiming;
  onChange: (timing: TriggerTiming) => void;
  interactionType: TriggerInteractionType;
};

export const TimingSection = ({timing, onChange, interactionType}: TimingSectionProps) => (
  <>
    <SectionTitle>Timing</SectionTitle>
    <RadioGroup
      value={timing}
      onChange={(e) => onChange(e.target.value as TriggerTiming)}
      sx={{display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5}}
    >
      {(Object.keys(TIMING_DISPLAY) as TriggerTiming[]).map((option) => {
        const selected = option === timing;
        return (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio sx={{p: 0, mr: 1.25, mt: "2px"}} />}
            sx={{
              m: 0,
              alignItems: "flex-start",
              p: "12px 14px",
              border: `1px solid ${selected ? Colors.blue._700 : Colors.tertiary._300}`,
              borderRadius: "8px",
              backgroundColor: selected ? Colors.blue._100 : Colors.bw.white,
            }}
            label={
              <Box>
                <Text size={14} variant="inter500">
                  {TIMING_DISPLAY[option].label}
                </Text>
                {TIMING_DISPLAY[option].description && (
                  <Text size={12} sx={{color: Colors.grey._600}}>
                    {TIMING_DISPLAY[option].description}
                  </Text>
                )}
                {option === "working_hours" && OWN_PAYMENT_INTERACTION_TYPES.has(interactionType) && (
                  <Text size={12} sx={{color: Colors.grey._600}}>
                    A customer-initiated payment is always sent right away.
                  </Text>
                )}
              </Box>
            }
          />
        );
      })}
    </RadioGroup>
  </>
);

type ChannelsSectionProps = {
  interactionType: TriggerInteractionType;
  condition: TriggerCondition;
  templates: ReachTemplateOption[];
  sendText: boolean;
  onSendTextChange: (sendText: boolean) => void;
  onChange: (patch: Partial<TriggerCondition>) => void;
};

export const ChannelsSection = ({
  interactionType,
  condition,
  templates,
  sendText,
  onSendTextChange,
  onChange,
}: ChannelsSectionProps) => {
  const [kind, setKind] = useState<TextKind>("sms");
  const selectedText = templates.find((t) => t.id === condition.text_template_id);
  const selectedEmail = templates.find((t) => t.id === condition.email_template_id);

  const openPreview = (initialTab: PreviewTab) =>
    NiceModal.show(TriggerPreviewDialog, {
      interactionType,
      emailTemplate: selectedEmail ?? null,
      textTemplate: sendText ? selectedText ?? null : null,
      account: null,
      initialTab,
    });

  useEffect(() => {
    if (selectedText) setKind(textKindOf(selectedText.content_channel));
  }, [selectedText]);

  const changeKind = (next: TextKind) => {
    setKind(next);
    if (selectedText && textKindOf(selectedText.content_channel) !== next) {
      onChange({text_template_id: null});
    }
  };

  const sending = condition.outcome === "send";

  return (
    <>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
        <SectionTitle>Channels</SectionTitle>
        <SegmentedToggle
          value={condition.outcome}
          options={OUTCOME_OPTIONS}
          onChange={(outcome) => onChange({outcome})}
        />
      </Box>
      {!sending ? (
        <Text size={13} sx={{color: Colors.grey._700}}>
          Accounts that match this condition get no message, and no condition below it is checked.
        </Text>
      ) : (
        <>
          <Box sx={{...listCardSx, ...rowSx}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.25, width: 180, flexShrink: 0}}>
              <LockOutlinedIcon sx={{fontSize: 16, color: Colors.grey._400}} />
              <Text size={14} variant="inter500">
                Email
              </Text>
              <Text size={12} sx={{color: Colors.grey._500}}>
                Always
              </Text>
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
              <TemplatePicker
                placeholder="Pick an email template"
                templates={templates}
                channels={TEMPLATE_SLOT_CHANNELS.email}
                value={condition.email_template_id}
                onChange={(id) => onChange({email_template_id: id})}
              />
            </Box>
            <PreviewButton disabled={!selectedEmail} onClick={() => openPreview("email")} />
            <OpenTemplateButton templateId={selectedEmail?.id} />
          </Box>

          <Box sx={{...listCardSx, ...rowSx}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, width: 180, flexShrink: 0}}>
              <FormControlLabel
                sx={{m: 0}}
                control={
                  <Checkbox
                    checked={sendText}
                    onChange={(e) => onSendTextChange(e.target.checked)}
                    sx={{p: 0, mr: 1}}
                  />
                }
                label={
                  <Text size={14} variant="inter500">
                    Text
                  </Text>
                }
              />
              <SegmentedToggle value={kind} options={TEXT_KIND_OPTIONS} onChange={changeKind} disabled={!sendText} />
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
              <TemplatePicker
                placeholder={sendText ? "Pick a text template" : "No text message"}
                templates={templates}
                channels={TEXT_KIND_CHANNELS[kind]}
                value={condition.text_template_id}
                onChange={(id) => onChange({text_template_id: id})}
                showChannel
                disabled={!sendText}
              />
            </Box>
            <PreviewButton disabled={!sendText || !selectedText} onClick={() => openPreview("text")} />
            <OpenTemplateButton templateId={sendText ? selectedText?.id : undefined} />
          </Box>
        </>
      )}
    </>
  );
};
